import { useEffect, useState } from 'react';
import { fetchVersion } from '@/api/version';
import { config } from '@/config';

// useVersionPoll compares the release SHA baked into THIS bundle at build time
// (config.sentryRelease) against the release currently advertised by the
// server's version.json. `updateAvailable` flips true the moment a newer SPA
// build has been deployed than the one this tab is running.
//
// Anchoring on the baked release (rather than the first value we happen to
// observe) is what makes this correct across the smart-deploy split: the
// comparison is "what this tab is running" vs "what's deployed now", both
// scoped to the SPA bundle and independent of the API's deploy cadence.
//
// Polling is gentle (one minute), pauses while the tab is hidden (Page
// Visibility API), and runs an immediate check on tab focus so a user who
// returns after a deploy is upgraded within ~a second. Local/dev builds have
// no baked release (config.sentryRelease === ''), so polling is skipped
// entirely — no /version.json 404 spam, no false positives.

const POLL_INTERVAL_MS = 60_000;

export interface UseVersionPollResult {
  /** True once the server advertises a different SPA build than this tab loaded with. */
  updateAvailable: boolean;
  /** Release SHA baked into this running bundle (null in dev). */
  loadedVersion: string | null;
  /** Latest release SHA advertised by the server's version.json. */
  latestVersion: string | null;
}

export function useVersionPoll(): UseVersionPollResult {
  const loadedVersion = config.sentryRelease || null;
  const [latestVersion, setLatestVersion] = useState<string | null>(null);

  useEffect(() => {
    // No baked release (local dev / unsealed build) — nothing meaningful to
    // compare against, so don't poll.
    if (!loadedVersion) return;

    let cancelled = false;
    let timer: number | undefined;

    const check = async () => {
      try {
        const { release } = await fetchVersion();
        if (cancelled || !release) return;
        setLatestVersion(release);
      } catch {
        // Network blips and transient 404/5xx are expected during a deploy
        // window — silently retry on the next tick.
      }
    };

    const schedule = () => {
      if (typeof document !== 'undefined' && document.hidden) return;
      timer = window.setTimeout(async () => {
        await check();
        schedule();
      }, POLL_INTERVAL_MS);
    };

    const onVisibility = () => {
      if (document.hidden) {
        if (timer !== undefined) {
          window.clearTimeout(timer);
          timer = undefined;
        }
      } else {
        void check();
        schedule();
      }
    };

    void check();
    schedule();
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      cancelled = true;
      if (timer !== undefined) window.clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [loadedVersion]);

  const updateAvailable =
    loadedVersion !== null &&
    latestVersion !== null &&
    loadedVersion !== latestVersion;

  return { updateAvailable, loadedVersion, latestVersion };
}
